import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Input from '../../../../components/Input';
import Button from '../../../../components/Button';
import Toast from '../../../../components/Toast';
import Spinner from '../../../../components/Spinner';
import ImageUpload from '../../../../components/ImageUpload';
import StatusBadge from '../../../../components/StatusBadge';
import useFetch from '../../../../hooks/useFetch';
import useUnsavedChanges from '../../../../hooks/useUnsavedChanges';
import { usePermission } from '../../../../hooks/usePermission';
import ProductService, { ProductFormPayload } from '../../../../services/product.service';
import MasterService from '../../../../services/master.service';
import ProductModuleVisibility from '../ProductModuleVisibility';
import { Modules, AuthRoutes } from '../../../../enums';
import { API_URL } from '../../../../utils/apiConstant';

interface TenantStatusOption {
  id: number;
  status_name: string;
  colour_code: string;
}

const DEFAULT_COLOUR = '#0f172a';
const HEX_COLOUR_REGEX = /^#[0-9A-Fa-f]{6}$/;

const initialForm: ProductFormPayload = {
  product_code: '',
  product_name: '',
  description: '',
  logo_url: '',
  theme_colour: DEFAULT_COLOUR,
  status_id: undefined,
  display_order: 0,
  notes: '',
};

const ProductForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const edit = !!id;
  const canManageModules = usePermission('master:manage');

  const [formData, setFormData] = useState<ProductFormPayload>(initialForm);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(edit);
  const [isDirty, setIsDirty] = useState(false);

  useUnsavedChanges(isDirty);

  const [activeStatuses, setActiveStatuses] = useState<TenantStatusOption[]>([]);

  useFetch({
    service: MasterService,
    func: 'list',
    payload: { type: 'tenantStatuses', includeDeleted: false, limit: 100 },
    dependency: [],
    onSuccess: (data: any) => setActiveStatuses(data?.items ?? []),
  });

  useEffect(() => {
    if (!id) {
      setFormData(initialForm);
      return;
    }
    setFetching(true);
    ProductService.get(id)
      .then((response: any) => {
        const row = response?.data ?? {};
        setFormData({
          product_code: row.product_code ?? '',
          product_name: row.product_name ?? '',
          description: row.description ?? '',
          logo_url: row.logo_url ?? '',
          theme_colour: row.theme_colour ?? DEFAULT_COLOUR,
          status_id: row.status_id ?? undefined,
          display_order: row.display_order ?? 0,
          notes: row.notes ?? '',
        });
      })
      .finally(() => setFetching(false));
  }, [id]);

  const validate = useCallback(
    (data: ProductFormPayload) => {
      const nextErrors: Record<string, string> = {};
      if (!data.product_code.trim()) nextErrors.product_code = t('masters.products.form.pageTexts.fields.productCode.errors.required');
      if (!data.product_name.trim()) nextErrors.product_name = t('masters.products.form.pageTexts.fields.productName.errors.required');
      if (!data.theme_colour || !HEX_COLOUR_REGEX.test(data.theme_colour)) {
        nextErrors.theme_colour = t('masters.products.form.pageTexts.fields.themeColour.errors.invalid');
      }
      if (!data.status_id) nextErrors.status_id = t('masters.products.form.pageTexts.fields.status.errors.required');
      return nextErrors;
    },
    [t]
  );

  const handleChange = useCallback((e: any, name: string) => {
    setIsDirty(true);
    const value = name === 'status_id' || name === 'display_order' ? Number(e.target.value) : e.target.value;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleLogoUploaded = useCallback((fileName: string) => {
    setIsDirty(true);
    setFormData((prev) => ({ ...prev, logo_url: fileName }));
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e && e.preventDefault();
      const nextErrors = validate(formData);
      setErrors(nextErrors);
      if (Object.keys(nextErrors).length > 0) return;

      setLoading(true);
      try {
        const payload = { ...formData, display_order: Number(formData.display_order) || 0 };

        if (edit && id) {
          await ProductService.update(id, payload);
          toast.success(<Toast message={t('masters.products.form.pageTexts.editSuccess')} />, { icon: false });
        } else {
          await ProductService.create(payload);
          toast.success(<Toast message={t('masters.products.form.pageTexts.addSuccess')} />, { icon: false });
        }
        setIsDirty(false);
        navigate(`${Modules.masters}${AuthRoutes.productMaster}`);
      } catch (err) {
        setLoading(false);
        return;
      }
      setLoading(false);
    },
    [formData, edit, id, navigate, validate, t]
  );

  if (fetching) {
    return (
      <div className="card-header text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="card mb-6">
      <h5 className="card-header">
        {edit ? t('masters.products.form.pageTexts.editTitle') : t('masters.products.form.pageTexts.addTitle')}
      </h5>
      <hr className="m-0" />
      <form className="card-body row g-4" onSubmit={handleSubmit}>
        <div className="col-md-3">
          <ImageUpload
            currentUrl={formData.logo_url ?? null}
            baseUrl={`${API_URL}/uploads/product-logos`}
            uploadFn={ProductService.uploadLogo}
            onSuccess={handleLogoUploaded}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="product_code"
            name="product_code"
            label={t('masters.products.form.pageTexts.fields.productCode.label')}
            placeholder={t('masters.products.form.pageTexts.fields.productCode.placeholder')}
            required
            value={formData.product_code}
            error={!!errors.product_code}
            errorMessage={errors.product_code}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-5">
          <Input
            id="product_name"
            name="product_name"
            label={t('masters.products.form.pageTexts.fields.productName.label')}
            placeholder={t('masters.products.form.pageTexts.fields.productName.placeholder')}
            required
            value={formData.product_name}
            error={!!errors.product_name}
            errorMessage={errors.product_name}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6">
          <Input
            id="description"
            name="description"
            label={t('masters.products.form.pageTexts.fields.description.label')}
            placeholder={t('masters.products.form.pageTexts.fields.description.placeholder')}
            value={formData.description}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label d-block">{t('masters.products.form.pageTexts.fields.themeColour.label')}</label>
          <div className="d-flex align-items-center gap-2">
            <input
              type="color"
              value={HEX_COLOUR_REGEX.test(formData.theme_colour ?? '') ? formData.theme_colour : DEFAULT_COLOUR}
              onChange={(e) => {
                setIsDirty(true);
                setFormData((prev) => ({ ...prev, theme_colour: e.target.value }));
              }}
            />
            <Input
              id="theme_colour"
              name="theme_colour"
              placeholder="#0f172a"
              value={formData.theme_colour}
              error={!!errors.theme_colour}
              errorMessage={errors.theme_colour}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="col-md-3">
          <label className="form-label d-block" htmlFor="status_id">
            {t('masters.products.form.pageTexts.fields.status.label')}
          </label>
          <select
            id="status_id"
            name="status_id"
            className={`form-select ${errors.status_id ? 'is-invalid' : ''}`}
            value={formData.status_id ?? ''}
            onChange={(e) => handleChange(e, 'status_id')}
          >
            <option value="">{t('masters.products.form.pageTexts.fields.status.placeholder')}</option>
            {activeStatuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.status_name}
              </option>
            ))}
          </select>
          {formData.status_id && (
            <div className="mt-2">
              {(() => {
                const selected = activeStatuses.find((s) => s.id === formData.status_id);
                return selected ? <StatusBadge label={selected.status_name} colour={selected.colour_code} /> : null;
              })()}
            </div>
          )}
        </div>
        <div className="col-md-2">
          <Input
            id="display_order"
            name="display_order"
            type="number"
            min="0"
            label={t('masters.products.form.pageTexts.fields.displayOrder.label')}
            placeholder="0"
            value={formData.display_order}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-4">
          <Input
            id="notes"
            name="notes"
            label={t('masters.products.form.pageTexts.fields.notes.label')}
            placeholder={t('masters.products.form.pageTexts.fields.notes.placeholder')}
            value={formData.notes}
            onChange={handleChange}
          />
        </div>
        <div className="col-12 d-flex gap-2 justify-content-end">
          <Button
            type="button"
            className="btn btn-outline-secondary"
            text={t('masters.products.form.pageTexts.buttons.cancel')}
            onClick={() => navigate(`${Modules.masters}${AuthRoutes.productMaster}`)}
          />
          <Button
            type="submit"
            className="btn btn-primary"
            text={t('masters.products.form.pageTexts.buttons.save')}
            loading={loading}
          />
        </div>
      </form>
      {edit && canManageModules && id && <ProductModuleVisibility productId={id} />}
    </div>
  );
};

export default ProductForm;
